import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { User } from "firebase/auth";
import { card, primaryBtn, secondaryBtn } from "../styles";
import { useIsMobile } from "../hooks/useIsMobile";
import { ALL_LEAGUE_MODES, MODE_CONTENT, MODE_COLOR_VAR, LeagueMode } from "./createLeague/modeContent";
import type { AuthModalMode } from "../components/AuthModal";
import HowToPlayModal from "../components/HowToPlayModal";

interface Props {
  user?: User | null;
  onSignIn: (mode?: AuthModalMode) => void;
  /** True right after AuthModal reports a successful sign-in/register. */
  pendingDashboardRedirect?: boolean;
  onDashboardRedirectHandled?: () => void;
}

interface DemoPlayer {
  rank: number;
  name: string;
  team: string;
  delta: string;
  score: number;
  isYou?: boolean;
}

const RANK_COLORS = ["oklch(0.75 0.16 60)", "var(--color-text-dim)", "oklch(0.65 0.20 340)", "var(--color-text-dim)"];

function DemoLeaderboard() {
  const { t } = useTranslation();
  const name = t("landing.demoLeaderboard.name");
  const meta = t("landing.demoLeaderboard.meta");
  const live = t("landing.demoLeaderboard.live");
  const players = t("landing.demoLeaderboard.players", { returnObjects: true }) as DemoPlayer[];

  return (
    <div style={{ ...card, padding: 0, overflow: "hidden" }}>
      <div style={{ padding: "1.1rem 1.25rem", display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
        <div>
          <div style={{ fontSize: "0.98rem", fontWeight: 700, color: "var(--color-text)" }}>{name}</div>
          <div style={{ fontSize: "0.78rem", color: "var(--color-text-muted)" }}>{meta}</div>
        </div>
        <span style={{
          fontSize: "0.65rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em",
          background: "var(--color-status-active-bg)", color: "var(--color-status-active-text)",
          borderRadius: 6, padding: "3px 8px",
        }}>
          {live}
        </span>
      </div>
      <div>
        {players.map((p, i) => (
          <div
            key={p.rank}
            style={{
              display: "flex", alignItems: "center", gap: "0.75rem",
              padding: "0.7rem 1.25rem",
              borderTop: "1px solid var(--color-border-subtle)",
              background: p.isYou ? "var(--color-accent-glow)" : "transparent",
            }}
          >
            <span style={{
              width: 22, height: 22, borderRadius: "50%", flexShrink: 0,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: "0.72rem", fontWeight: 700,
              background: RANK_COLORS[i % RANK_COLORS.length],
              color: "#0d0820",
            }}>
              {p.rank}
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: "0.85rem", fontWeight: p.isYou ? 700 : 600, color: p.isYou ? "var(--color-accent)" : "var(--color-text)" }}>
                {p.name}
              </div>
              <div style={{ fontSize: "0.72rem", color: "var(--color-text-muted)" }}>{p.team}</div>
            </div>
            <span style={{ fontSize: "0.72rem", fontWeight: 700, color: "var(--color-success)" }}>
              ▲{p.delta.replace("+", "")}
            </span>
            <span style={{ fontSize: "0.95rem", fontWeight: 700, color: "var(--color-text)", minWidth: 24, textAlign: "right" }}>
              {p.score}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function WelcomePage({ user, onSignIn, pendingDashboardRedirect, onDashboardRedirectHandled }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const ctaAction = user ? () => navigate("/overview") : () => onSignIn("register");
  const [howToPlayMode, setHowToPlayMode] = useState<LeagueMode | null>(null);

  useEffect(() => {
    if (user && pendingDashboardRedirect) {
      navigate("/overview");
      onDashboardRedirectHandled?.();
    }
  }, [user, pendingDashboardRedirect, navigate, onDashboardRedirectHandled]);

  return (
    <div className="sg-fade-in" style={{ maxWidth: 1100, margin: "0 auto", padding: isMobile ? "2rem 1rem 3rem" : "3.5rem 1.5rem 4rem" }}>

      {/* ── Hero ── */}
      <div style={{
        display: "grid",
        gridTemplateColumns: isMobile ? "1fr" : "1fr 380px",
        gap: isMobile ? "2rem" : "3rem",
        alignItems: "center",
        marginBottom: isMobile ? "3rem" : "4rem",
      }}>
        <div>
          <div style={{ display: "flex", gap: "0.6rem", flexWrap: "wrap", marginBottom: "1.25rem" }}>
            <div style={{
              display: "inline-flex", alignItems: "center", gap: "0.4rem",
              fontSize: "0.75rem", fontWeight: 600, color: "var(--color-warning)",
              background: "color-mix(in srgb, var(--color-warning) 14%, transparent)",
              border: "1px solid var(--color-warning)",
              borderRadius: 999, padding: "0.3rem 0.8rem",
            }}>
              <span style={{ width: 6, height: 6, borderRadius: "50%", background: "var(--color-warning)" }} />
              {t("landing.badge")}
            </div>
          </div>

          <h1 style={{
            margin: "0 0 1rem",
            fontFamily: "var(--font-display)",
            fontSize: isMobile ? "2.4rem" : "3.4rem",
            fontWeight: 800,
            letterSpacing: "0.01em",
            lineHeight: 1.08,
            color: "var(--color-text)",
          }}>
            {t("landing.hero.headlineLine1")}<br />
            <span style={{ color: "var(--color-accent)" }}>{t("landing.hero.headlineLine2")}</span>
          </h1>

          <p style={{
            margin: "0 0 1.75rem",
            fontSize: isMobile ? "1rem" : "1.1rem",
            color: "var(--color-text-muted)",
            lineHeight: 1.55,
            maxWidth: 480,
          }}>
            {t("landing.hero.subtitle")}
          </p>

          <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap", marginBottom: "1.25rem" }}>
            <button onClick={ctaAction} style={{ ...primaryBtn, fontSize: "1rem", padding: "0.75rem 1.75rem" }}>
              {t(user ? "landing.hero.ctaMember" : "landing.hero.cta")} →
            </button>
            <Link
              to="/about"
              style={{ ...secondaryBtn, fontSize: "1rem", padding: "0.75rem 1.75rem", textDecoration: "none", display: "inline-flex", alignItems: "center" }}
            >
              {t("landing.hero.findOutMore")}
            </Link>
          </div>

          <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap" }}>
            {[t("landing.hero.pillFree"), t("landing.hero.pillModes")].map((label) => (
              <span key={label} style={{
                display: "inline-flex", alignItems: "center", gap: "0.4rem",
                fontSize: "0.78rem", color: "var(--color-text-muted)",
                background: "var(--color-surface-alt)", borderRadius: 999, padding: "0.3rem 0.75rem",
              }}>
                <span style={{ width: 5, height: 5, borderRadius: "50%", background: "var(--color-success)" }} />
                {label}
              </span>
            ))}
          </div>
        </div>

        <DemoLeaderboard />
      </div>

      {/* ── Game modes ── */}
      <div style={{ marginBottom: isMobile ? "3rem" : "4rem" }}>
        <div style={{
          fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase",
          letterSpacing: "0.12em", color: "var(--color-text-dim)", marginBottom: "0.6rem",
        }}>
          {t("landing.modes.eyebrow", { count: ALL_LEAGUE_MODES.length })}
        </div>
        <h2 style={{
          margin: "0 0 1.5rem",
          fontFamily: "var(--font-display)",
          fontSize: isMobile ? "1.5rem" : "1.9rem",
          fontWeight: 800,
          color: "var(--color-text)",
        }}>
          {t("landing.modes.heading")}
        </h2>

        <div style={{
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr 1fr" : `repeat(${ALL_LEAGUE_MODES.length}, 1fr)`,
          gap: "0.85rem",
        }}>
          {ALL_LEAGUE_MODES.map((mode) => {
            const Icon = MODE_CONTENT[mode].icon;
            const color = MODE_COLOR_VAR[mode];
            const item = t(`landing.modes.items.${mode}`, { returnObjects: true }) as { name: string; desc: string };
            return (
              <button
                key={mode}
                onClick={() => setHowToPlayMode(mode)}
                style={{
                  ...card,
                  padding: "1.1rem 1rem",
                  borderTop: `3px solid ${color}`,
                  display: "flex",
                  flexDirection: "column",
                  gap: "0.5rem",
                  textAlign: "left" as const,
                  cursor: "pointer",
                  font: "inherit",
                }}
              >
                <span style={{ color }}>
                  <Icon />
                </span>
                <span style={{ fontSize: "0.9rem", fontWeight: 700, color: "var(--color-text)" }}>
                  {item.name}
                </span>
                <p style={{ margin: 0, fontSize: "0.78rem", color: "var(--color-text-muted)", lineHeight: 1.45 }}>
                  {item.desc}
                </p>
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Final CTA ── */}
      <div style={{
        background: "var(--color-accent)",
        borderRadius: 20,
        padding: isMobile ? "2.5rem 1.5rem" : "3.5rem 2rem",
        textAlign: "center",
      }}>
        <h2 style={{
          margin: "0 0 0.75rem",
          fontFamily: "var(--font-display)",
          fontSize: isMobile ? "1.7rem" : "2.2rem",
          fontWeight: 800,
          color: "var(--color-btn-primary-text)",
        }}>
          {t(user ? "landing.finalCta.headingMember" : "landing.finalCta.heading")}
        </h2>
        <p style={{
          margin: "0 auto 1.75rem",
          fontSize: isMobile ? "0.9rem" : "1rem",
          color: "var(--color-btn-primary-text)",
          opacity: 0.85,
          maxWidth: 440,
          lineHeight: 1.5,
        }}>
          {t(user ? "landing.finalCta.subtitleMember" : "landing.finalCta.subtitle")}
        </p>
        <button
          onClick={ctaAction}
          style={{
            ...primaryBtn,
            background: "var(--color-btn-primary-text)",
            color: "var(--color-accent)",
            fontSize: "1rem",
            padding: "0.75rem 1.9rem",
          }}
        >
          {t(user ? "landing.finalCta.ctaMember" : "landing.finalCta.cta")} →
        </button>
      </div>

      {howToPlayMode && (
        <HowToPlayModal mode={howToPlayMode} onClose={() => setHowToPlayMode(null)} />
      )}

    </div>
  );
}
